// AccessGuard — redirects authenticated users away from URLs their roles
// don't grant access to. The check is additive: a user's effective access
// is the UNION of every role's surface list (see hooks/useUserRoles.ts).
// Adding more roles to a user strictly *adds* access, never subtracts.
//
// Behaviour:
//   - While the role lookup is in flight: render children. Any flash of
//     content is preferable to a blocking spinner that would also block
//     legitimate operators.
//   - Once roles resolve and the current pathname is NOT in the user's
//     allowed-surface set: replace the route with the first surface they
//     CAN reach (so the redirect lands somewhere useful, not on /login).
//   - Universal surfaces (/login, /set-password, /docs, /settings/security,
//     etc.) are always permitted regardless of role.
//
// The component name `SampleCoordOnlyGuard` is kept as the file name to
// avoid a rename-induced merge headache; the export is the new
// `AccessGuard` plus a deprecated alias.

import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { useUserRoles } from "@/hooks/useUserRoles";

const FALLBACK_SURFACE_PRIORITY = [
  "/library",
  "/dashboard",
  "/customers",
  "/orders",
  "/qa",
  "/developments",
  "/docs",
];

/** Pick the most natural landing page for a denied redirect — first allowed
 *  surface from FALLBACK_SURFACE_PRIORITY that the user can reach, falling
 *  back to /docs (universal) if somehow none match. */
function pickFallback(allowed: Set<string>): string {
  if (allowed.has("*")) return "/dashboard";
  for (const candidate of FALLBACK_SURFACE_PRIORITY) {
    if (allowed.has(candidate)) return candidate;
  }
  return "/docs";
}

export function AccessGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { loading, isAllowed, allowedSurfaces } = useUserRoles();

  if (loading) return <>{children}</>;

  if (!isAllowed(location.pathname)) {
    return <Navigate to={pickFallback(allowedSurfaces)} replace />;
  }

  return <>{children}</>;
}

/** @deprecated Renamed to AccessGuard once the model went additive. */
export const SampleCoordOnlyGuard = AccessGuard;
